import LoadingSpinner from "@/components/util/LoadingSpinner";
import { api } from "@/lib/api/api";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { useAuthenticated } from "@gettactic/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import React, { useRef } from "react";
import { MenuItemLink } from "../layouts/AppLayout/AppTopNav";
import { DropdownMenuItem } from "@v2/components/ui/dropdown-menu";
import { SparklesIcon } from "lucide-react";
import { isTacticStaff } from "@/lib/featureFlags";
import { AuthenticatedUser, shouldShowV2SwitchForAllUsers } from "@gettactic/api";
import { navUserClassNames } from "../layouts/v2/layout/nav-user";
import { getDesignSystemVersion } from "@webV2/helpers/isDesignSystem2";
import { USE_ONLY_LOCAL_STORAGE_FOR_DS } from "@gettactic/helpers/src/hooks/useGetAuthenticatedUser";
import { canScheduleOthers } from "@/lib/permissions";
import { getSunset } from "@gettactic/helpers";

import {
  ZendeskChat,
  ZendeskChatRef
} from "@gettactic/helpers/src/ZendeskChat";

const showTheSwitch = (authenticatedUser: AuthenticatedUser): boolean => {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  if (shouldShowV2SwitchForAllUsers(authenticatedUser.organization?.slug)) {
    return true;
  }
  // TODO - JUST FOR HPE TESTING PURPOSES - REMOVE LATER
  const allowedSwitchEmails = [
    "jiri.tot@hpe.com",
    "chua.nicholas@hpe.com",
    "radhika.verma@hpe.com",
    "tracy.farber@hpe.com",
    "saul.parra@hpe.com",
    "deanna.l.kwong@hpe.com",
    "kevin.strebel@hpe.com",
    "kevin.strebelhpe@gmail.com",
    "dan_user_3@mailinator.com",

    // List from Schyler
    "kchristman@mcknze.com",
    "mdubovoy@mcknze.com",
    "mad@mcknze.com",
    "hweiss@altair.com",
    "joanna.vanbrunt@judicial.state.co.us",
    "peter.duda@judicial.state.co.us",
    "lunetta.lach@freeagent.com",
    "becky.robertson@freeagent.com",
    "kfluker@bmss.com",
    "rcarter@bmss.com",
    "kaki@bmss.com",
    "aleveille@bmss.com",
    "dgilleland@abacustechnologies.com"
  ];

  if (
    authenticatedUser.user?.email &&
    allowedSwitchEmails.includes(authenticatedUser.user?.email)
  ) {
    return true;
  }

  const isAdmin = canScheduleOthers(authenticatedUser);
  if (isTacticStaff(authenticatedUser) || isAdmin) {
    return true;
  }
  try {
    const value = localStorage.getItem("redesign-2025");
    return value === "true";
  } catch (error) {
    console.error("Error accessing localStorage:", error);
    return false;
  }
  // return false;
};

export default function DesignSwitcher({
  isNewMenu,
}: {
  isNewMenu: boolean;
}) {
  const [backToClassicWarning, setBackToClassicWarning] = React.useState(false);

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const queryClient = useQueryClient();
  const isBeingImpersonated = authenticatedUser?.user?.is_being_impersonated;
  // const value = authenticatedUser.designSystemVersion;
  const value = getDesignSystemVersion();
  const [isSwitchingToV2, setIsSwitchingToV2] = React.useState(false);
  const toggleDesignSystem = useMutation(async () => {
    setIsSwitchingToV2(true);
    const appliedDesignSystem = value === "v1" ? "v2" : "v1";

    // if ds is not set yet, assume it's v1, so we'll switch to v2
    setDesignSystemVersion.mutate(value ? appliedDesignSystem : "v2");
  });

  const setDesignSystemVersion = useMutation(
    async (appliedDesignSystem: "v1" | "v2") => {
      // don't save to the server if we're using only local storage
      // condition will be removed in the future
      // however, currently we also don't want to save to the server
      // if user is being impersonated
      if (!USE_ONLY_LOCAL_STORAGE_FOR_DS && !isBeingImpersonated) {
        await api.client.userSettings.updateDesignSystem(appliedDesignSystem);
        await queryClient.invalidateQueries([
          "user.properties.design_system_version"
        ]);
      }

      localStorage.setItem("designSystemVersion", appliedDesignSystem);

      // have to trigger reload to apply the new design system version
      if (USE_ONLY_LOCAL_STORAGE_FOR_DS) {
        setTimeout(() => {
          // give it a moment to work with ls
          window.location.reload();
        }, 1000);
      }
    }
  );

  const isV2 = value === "v2";

  const canShowSwitch = showTheSwitch(authenticatedUser) || isV2;
  if (!canShowSwitch) {
    return null;
  }
  const loading = toggleDesignSystem.isLoading;

  const handleClick = () => {
    if (isV2) {
      setBackToClassicWarning(true);
      return;
    }
    toggleDesignSystem.mutate();
  };

  const ButtonComponent =
    isV2 && isNewMenu ? (
      <DropdownMenuItem
        className={navUserClassNames.MenuItem}
        onSelect={(e) => {
          e.preventDefault();
          handleClick();
        }}
      >
        <SparklesIcon className="size-4" />
        <span className="text-primary">Back to classic design</span>
      </DropdownMenuItem>
    ) : (
      <MenuItemLink onClick={handleClick}>Apply new design</MenuItemLink>
    );
  const zendeskChatRef = useRef<ZendeskChatRef>(null);

  return (
    <>
      {loading || isSwitchingToV2 ? (
        <MenuItemLink>
          <div className="flex items-center">
            Apply new design <LoadingSpinner className="ml-2" />
          </div>
        </MenuItemLink>
      ) : (
        ButtonComponent
      )}

      <ConfirmModal
        isOpen={backToClassicWarning}
        onOpenChange={(isOpen) => {
          setBackToClassicWarning(isOpen);
        }}
        onConfirm={() => {
          setBackToClassicWarning(false);
          toggleDesignSystem.mutate();
        }}
        onCancel={() => setBackToClassicWarning(false)}
        message={
          <div>
            <p>
              The classic design is expected to be sunset on{" "}
              <strong className="text-bold">{getSunset()}</strong>. At that
              time, all users will be automatically migrated to our new design.
              You can continue using the classic design until then.
            </p>
            <p>
              We value your feedback! If there’s anything you miss or want to
              share about your experience, please use the{" "}
              <button
                className="text-primary"
                onClick={() => zendeskChatRef.current?.openChat()}
              >
                support chat
              </button>
              .
            </p>
          </div>
        }
        title={
          <span className="font-bold text-2xl text-primary">
            Switch back to Classic Design
          </span>
        }
      />
      <ZendeskChat ref={zendeskChatRef} />
    </>
  );
}
